import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthContext";
import { DynamicIcon } from "lucide-react/dynamic";

const ProfileDropdown: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if user is admin
  const adminAuth0Ids = import.meta.env["VITE_ADMIN_AUTH0_IDS"] || "";
  const adminIdsList = adminAuth0Ids.split(",").map((id: string) => id.trim());
  const isAdmin = user?.auth0Id && adminIdsList.includes(user.auth0Id);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-md text-gray-300 hover:bg-gray-700 transition-colors"
        aria-label="Profile menu"
      >
        <DynamicIcon name="user" className="w-5 h-5" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-gray-800 rounded-md shadow-lg border border-gray-700 z-50">
          <div className="py-2">
            {/* User Info */}
            <div className="px-4 py-3 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold">
                  <DynamicIcon name="user" className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name || "User"}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              <button
                onClick={() => {
                  navigate("/app/profile");
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
              >
                <DynamicIcon name="settings" className="w-4 h-4" />
                Profile Settings
              </button>

              {/* Admin Dashboard (Only for admins) */}
              {isAdmin && (
                <button
                  onClick={() => {
                    navigate("/app/admin");
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2 text-sm text-indigo-300 hover:bg-gray-700 flex items-center gap-2"
                >
                  <DynamicIcon name="rocket" className="w-4 h-4" />
                  Admin Dashboard
                </button>
              )}

              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
              >
                <DynamicIcon name="log-out" className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
